'use client';

import React, { useEffect, useState, useRef } from 'react';
import Image from '@/lib/app-image';
import {
  useViewBookingMutation,
  useSendMailMutation,
} from '@flysync/shared-redux/services/booking';
import { useSelector } from 'react-redux';
import { useToast } from '@/hooks/use-toast';
import { useSearchParams } from '@/lib/navigation';
import { Mail, Download } from 'lucide-react';
import { config, getTemplateAssetPath } from '@/config';
import { getAirlineIconUrl } from '@/lib/airlineIcon';

const Ticket = () => {
  const searchParams = useSearchParams();
  const booking_id = searchParams.get('booking_id');
  const flightSearch = useSelector(
    (state: { flightSearch: any }) => state.flightSearch,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [ticketData, setTicketData] = useState<any>({});
  const [htmlEmailFormat, setHtmlEmailFormat] = useState<string>('');
  const [sendMail] = useSendMailMutation({});
  const { toast } = useToast();
  const [viewBooking] = useViewBookingMutation({
    fixedCacheKey: 'viewBooking',
  });
  const emailSentRef = useRef(false);
  const Loader = getTemplateAssetPath('loader');
  const apiCall = async () => {
    try {
      setIsLoading(true);
      emailSentRef.current = false; // Reset email sent flag for new booking
      const bookingId = booking_id
        ? Number(booking_id)
        : flightSearch?.booking_id;

      if (!bookingId) {
        throw new Error('No booking ID provided');
      }

      const data = {
        booking_id: bookingId,
      };

      const res: any = await viewBooking({
        data,
        auth: sessionStorage.getItem('token') || '',
      }).unwrap();

      const resData = res?.data?.data ? res.data.data : res.data;
      setTicketData(resData);

      // if (Object.keys(resData).length > 0) {
      //   window.history.back();
      // }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch ticket details',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Add viewport meta tag for mobile responsiveness
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content =
      'width=device-width, initial-scale=0.6, maximum-scale=1.0, user-scalable=yes';
    document.getElementsByTagName('head')[0].appendChild(meta);

    return () => {
      document.getElementsByTagName('head')[0].removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (booking_id || flightSearch?.booking_id) {
      apiCall();
    } else {
      window.history.back();
    }
  }, [booking_id, flightSearch?.booking_id]);

  useEffect(() => {
    if (!ticketData?.statusCode) return;

    const ticket = document.getElementById('ticket');
    if (!ticket) return;

    const html = `
      <div style="
        font-family: 'Arial', sans-serif;
        margin: 0;
        padding: 0;
        background-color: #f4f4f4;
      ">
        <div style="
          max-width: 100%;
          margin: 20px auto;
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
        ">
          ${ticket.innerHTML}
        </div>
      </div>
    `;

    setHtmlEmailFormat(html);
  }, [ticketData?.statusCode]);

  // Send email automatically if mailToCustomer flag is set and htmlEmailFormat is ready
  useEffect(() => {
    if (
      sessionStorage.getItem('mailToCustomer') === 'true' &&
      htmlEmailFormat &&
      ticketData?.statusCode &&
      !emailSentRef.current
    ) {
      // Use setTimeout to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        emailSentRef.current = true;
        sendTicketEmail();
      }, 500); // Small delay to ensure everything is ready

      return () => clearTimeout(timer);
    }
  }, [htmlEmailFormat, ticketData?.statusCode]);

  const sendTicketEmail = async () => {
    try {
      if (!htmlEmailFormat || !ticketData?.statusCode) {
        toast({
          title: 'Error',
          description: 'Ticket data not ready yet',
          variant: 'error',
        });
        return;
      }

      const departure = ticketData?.bookingDetails[0]?.departure || '';
      const arrival = ticketData?.bookingDetails[0]?.arrival || '';

      const mailData = {
        data: htmlEmailFormat,
        id: `${departure}-${arrival}`,
        email_id: ticketData?.bookingDetails[0]?.booking_email,
        email_type: 'eticket',
        flow: '',
        booking_id: flightSearch?.booking_id || Number(booking_id),
      };

      if (!mailData.email_id || !mailData.booking_id) {
        throw new Error('Missing required email information');
      }

      const response = await sendMail({
        data: mailData,
        auth: sessionStorage.getItem('token'),
      }).unwrap();

      if (response?.status?.type === 'success') {
        toast({
          title: 'Success',
          description: 'Ticket sent to your email successfully',
          variant: 'success',
        });
        // Clear mailToCustomer from sessionStorage after successful email send
        sessionStorage.removeItem('mailToCustomer');
      } else {
        toast({
          title: 'Error',
          description: 'Failed to send ticket to email',
          variant: 'error',
        });
        throw new Error('Failed to send email');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send ticket to email',
        variant: 'error',
      });
    }
  };

  const downloadTicket = async () => {
    try {
      if (!ticketData?.statusCode) {
        toast({
          title: 'Error',
          description: 'Ticket data not ready yet',
          variant: 'error',
        });
        return;
      }

      // Show loading toast
      toast({
        title: 'Preparing Download',
        description: 'Generating PDF...',
        variant: 'default',
      });

      // Dynamic import for html2canvas and jsPDF
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const ticketElement = document.getElementById('ticket');
      if (!ticketElement) {
        throw new Error('Ticket element not found');
      }

      // Create canvas from the ticket element
      const canvas = await html2canvas(ticketElement, {
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: ticketElement.scrollWidth,
        height: ticketElement.scrollHeight,
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Calculate dimensions to fit the content
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      // Add image to PDF
      pdf.addImage(
        imgData,
        'PNG',
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
      );

      // Generate filename
      const bookingId = ticketData?.bookingDetails[0]?.booking_id || 'ticket';
      const departure = ticketData?.bookingDetails[0]?.departure_city || '';
      const arrival = ticketData?.bookingDetails[0]?.arrival_city || '';
      const filename = `ticket-${bookingId}-${departure}-${arrival}.pdf`;

      // Download the PDF
      pdf.save(filename);

      toast({
        title: 'Success',
        description: 'Ticket downloaded successfully',
        variant: 'success',
      });
    } catch (error) {
      console.error('Download error:', error);

      // Fallback: Try to download as image
      try {
        const ticketElement = document.getElementById('ticket');
        if (ticketElement) {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(ticketElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
          });

          const link = document.createElement('a');
          link.download = `ticket-${ticketData?.bookingDetails[0]?.booking_id || 'ticket'}.png`;
          link.href = canvas.toDataURL();
          link.click();

          toast({
            title: 'Success',
            description: 'Ticket downloaded as image',
            variant: 'success',
          });
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback download error:', fallbackError);
      }

      toast({
        title: 'Error',
        description: 'Failed to download ticket. Please try again.',
        variant: 'error',
      });
    }
  };

  const getValidText = (value: any) => {
    if (typeof value !== 'string') return '';

    const trimmedValue = value.trim();
    return trimmedValue && trimmedValue.toLowerCase() !== 'undefined'
      ? trimmedValue
      : '';
  };

  const getAddressText = (address: any) => {
    if (!Array.isArray(address)) return '';

    return address
      .map((item: any) => getValidText(item))
      .filter(
        (item: string) => item && !item.toLowerCase().includes('undefined'),
      )
      .join(', ');
  };

  const adminName =
    getValidText(ticketData?.adminAddress?.adminName) ||
    getValidText(ticketData?.address_info?.agency_name);
  const adminPhone =
    getValidText(ticketData?.adminAddress?.adminPhone) ||
    getValidText(ticketData?.address_info?.phone);
  const adminEmail =
    getValidText(ticketData?.adminAddress?.adminEMail) ||
    getValidText(ticketData?.address_info?.email);
  const adminAddress =
    getAddressText(ticketData?.adminAddress?.address) ||
    getAddressText(ticketData?.address_info?.address);

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="mx-auto w-full max-w-[1200px] bg-white"
        style={{
          minWidth: '320px',
          overflowX: 'auto',
          WebkitTextSizeAdjust: '70%',
          textSizeAdjust: '70%',
        }}
      >
        <div className="flex flex-row items-center justify-between border-b p-2 sm:p-4">
          <div className="flex flex-col">
            <p className="break-words text-xs text-gray-600 sm:text-sm">
              {ticketData?.bookingDetails?.booking_id &&
                `Booking ID: ${ticketData.bookingDetails.booking_id}`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => downloadTicket()}
              className="flex items-center gap-1 whitespace-nowrap rounded-md bg-green-500 px-2 py-1 text-xs text-white transition-colors hover:bg-green-600 sm:gap-2 sm:px-4 sm:py-2 md:text-sm"
            >
              <Download className="h-3 w-3 sm:h-4 sm:w-4" />
              Download
            </button>
            <button
              onClick={() => sendTicketEmail()}
              className="hover:bg-primary/90 flex items-center gap-1 whitespace-nowrap rounded-md bg-primary px-2 py-1 text-xs text-white transition-colors sm:gap-2 sm:px-4 sm:py-2 md:text-sm"
            >
              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
              Send Mail
            </button>
          </div>
        </div>

        {isLoading && !ticketData?.adminLogo ? (
          <div className="flex h-screen items-center justify-center bg-white">
            <img src={Loader} alt="loader" width={100} className="h-64 w-64" />
          </div>
        ) : (
          <table className="w-full" id="ticket">
            <tr>
              <td className="w-full">
                <table width="100%">
                  <tr>
                    <td>
                      <table width="100%">
                        <tr>
                          <td style={{ padding: '0 40px' }}>
                            <table
                              width="100%"
                              className="space-y-2 py-2 sm:space-y-3 md:space-y-4"
                            >
                              {/* Logo and Header Section */}

                              <tr>
                                <td style={{ width: '10%' }}>
                                  {config.corporateName && (
                                    <div
                                      style={{
                                        width: '120px',
                                        height:
                                          config.corporateName === 'DMCLeisure'
                                            ? '160px'
                                            : '70px',
                                      }}
                                    >
                                      <img
                                        src={`/${config.corporateName}/logo.png`}
                                        alt="Agency Logo"
                                        style={{
                                          display: 'block',
                                          width: '100%',
                                          objectFit: 'contain',
                                          borderRadius: '4px',
                                        }}
                                        className="object-contain"
                                        crossOrigin="anonymous"
                                        onError={(e) => {
                                          // If logo load fails, show fallback image or placeholder
                                          const target =
                                            e.target as HTMLImageElement;
                                          // Try a fallback static image or placeholder text
                                          target.onerror = null; // prevent infinite loop
                                        }}
                                      />
                                    </div>
                                  )}
                                </td>
                                <td style={{ width: '33.33%' }}></td>
                                <td style={{ width: '33.33%' }}>
                                  {/* <div style={{ maxWidth: '300px' }}> */}
                                  <h3 className="sm:text-md text-xl font-semibold md:text-xl">
                                    {adminName || '-'}
                                  </h3>
                                  <div className="flex flex-col gap-1">
                                    <div className="flex flex-row gap-1 text-[10px] sm:text-xs md:text-sm">
                                      <div className="text-[10px] font-semibold sm:text-xs md:text-sm">
                                        {' '}
                                        Phone:{' '}
                                      </div>
                                      {adminPhone || '-'}
                                    </div>
                                    <div className="flex flex-row gap-1 text-[10px] sm:text-xs md:text-sm">
                                      <div className="text-[10px] font-semibold sm:text-xs md:text-sm">
                                        {' '}
                                        Email:{' '}
                                      </div>
                                      {adminEmail || '-'}
                                    </div>
                                    <div className="flex flex-row gap-1 text-[10px] sm:text-xs md:text-sm">
                                      <div className="text-[10px] font-semibold sm:text-xs md:text-sm">
                                        {' '}
                                        Address:{' '}
                                      </div>
                                      {adminAddress || '-'}
                                    </div>
                                  </div>
                                  {/* </div> */}
                                </td>
                              </tr>

                              {/* Booking Details Section */}
                              {/* <tr>
                                <td className="pt-2 sm:pt-3 md:pt-4">
                                  <span className="h-3 w-1 bg-primary sm:h-3.5 md:h-4"></span>
                                  Booking Details
                                </td>
                                <td className="text-xs font-semibold sm:text-sm md:text-base"></td>
                              </tr> */}

                              {/* Flight Details */}
                              <tr height="10px"></tr>
                              <tr>
                                <td style={{ width: '33.33%' }}>
                                  <h3 className="sm:text-md text-xl font-semibold md:text-xl">
                                    {' '}
                                    Booking Details
                                  </h3>
                                  <h3 className="text-sm font-semibold sm:text-base md:text-lg">
                                    {`${ticketData?.bookingDetails[0]?.departure_city} - ${ticketData?.bookingDetails[0]?.arrival_city}`}
                                  </h3>
                                  <h5 className="mt-1 text-[10px] sm:text-xs md:text-sm">
                                    {`${ticketData?.bookingDetails[0]?.departure_date} · ${ticketData?.bookingDetails[0]?.stops} · ${ticketData?.bookingDetails[0]?.duration}`}
                                  </h5>
                                </td>
                                <td style={{ width: '33.33%' }}></td>
                                <td
                                  style={{
                                    width: '33.33%',
                                    textAlign: 'right',
                                  }}
                                >
                                  <h3 className="text-xs font-semibold sm:text-sm md:text-base">
                                    Flight Ticket (
                                    {ticketData?.bookingDetails[0]?.stops})
                                  </h3>
                                  <div className="text-[10px] sm:text-xs md:text-sm">
                                    {`Booking ID: ${ticketData?.bookingDetails[0]?.booking_id} (Booked on ${ticketData?.bookingDetails[0]?.booking_time})`}
                                  </div>
                                </td>
                              </tr>
                              <tr height="15"></tr>
                            </table>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <table width="100%">
                              <tr>
                                <td
                                  style={{
                                    padding: '35px',
                                    backgroundColor:
                                      'var(--color-ticket-background-light)',
                                    width: '100%',
                                  }}
                                >
                                  <table width="100%">
                                    <tr>
                                      <td
                                        style={{
                                          padding: '20px',
                                          borderRadius: '16px',
                                          backgroundColor: '#fff',
                                        }}
                                      >
                                        {ticketData.bookingDetails.map(
                                          (booking: any) => {
                                            return (
                                              <table
                                                width="100%"
                                                key={`booking-${booking.booking_id}`}
                                              >
                                                <tr>
                                                  <td>
                                                    <table width="100%">
                                                      <tr>
                                                        <td>
                                                          <table width="100%">
                                                            <th></th>
                                                            {booking?.layOverData?.map(
                                                              (
                                                                flightData: any,
                                                                layoverIndex: number,
                                                                array: any[],
                                                              ) => {
                                                                const flightIndex =
                                                                  array.indexOf(
                                                                    flightData,
                                                                  );
                                                                return (
                                                                  <React.Fragment
                                                                    key={`layover-${layoverIndex}`}
                                                                  >
                                                                    <React.Fragment
                                                                      key={`flight-${layoverIndex}-${flightIndex}-${flightData?.flight_number}`}
                                                                    >
                                                                      <tr>
                                                                        <td>
                                                                          <table width="100%">
                                                                            <tr>
                                                                              <td
                                                                                style={{
                                                                                  width:
                                                                                    '20%',
                                                                                }}
                                                                              >
                                                                                <div
                                                                                  style={{
                                                                                    marginBottom:
                                                                                      '10px',
                                                                                    display:
                                                                                      'flex',
                                                                                    alignItems:
                                                                                      'center',
                                                                                    gap: '10px',
                                                                                  }}
                                                                                >
                                                                                  {flightData?.airline_code && (
                                                                                    <img
                                                                                      src={getAirlineIconUrl(
                                                                                        flightData?.airline_code,
                                                                                      )}
                                                                                      crossOrigin="anonymous"
                                                                                      alt={
                                                                                        flightData?.airline_code
                                                                                      }
                                                                                      className="h-7 w-7"
                                                                                    />
                                                                                  )}

                                                                                  <div
                                                                                    style={{
                                                                                      width:
                                                                                        'max-content',
                                                                                    }}
                                                                                  >
                                                                                    <table width="100%">
                                                                                      <tr>
                                                                                        <td
                                                                                          style={{
                                                                                            fontSize:
                                                                                              '14px',
                                                                                            fontWeight:
                                                                                              '600',
                                                                                          }}
                                                                                        >
                                                                                          {
                                                                                            flightData?.airline_name
                                                                                          }
                                                                                        </td>
                                                                                      </tr>
                                                                                      <tr>
                                                                                        <td
                                                                                          style={{
                                                                                            fontSize:
                                                                                              '12px',
                                                                                            width:
                                                                                              'max-width',
                                                                                          }}
                                                                                        >
                                                                                          {
                                                                                            flightData?.flight_number
                                                                                          }
                                                                                        </td>
                                                                                      </tr>
                                                                                    </table>
                                                                                  </div>
                                                                                </div>
                                                                                {flightData.pnr && (
                                                                                  <div
                                                                                    style={{
                                                                                      border:
                                                                                        '1px solid var(--color-primary-default)',
                                                                                      borderRadius:
                                                                                        '5px',
                                                                                      display:
                                                                                        'flex',
                                                                                      alignItems:
                                                                                        'center',
                                                                                      gap: '6px',
                                                                                      width:
                                                                                        'max-content',
                                                                                    }}
                                                                                  >
                                                                                    <div
                                                                                      style={{
                                                                                        fontSize:
                                                                                          '12px',
                                                                                        fontWeight:
                                                                                          '600',
                                                                                        backgroundColor:
                                                                                          'var(--color-primary-default)',
                                                                                        color:
                                                                                          '#fff',
                                                                                        padding:
                                                                                          '5px',
                                                                                        borderTopLeftRadius:
                                                                                          '5px',
                                                                                        borderBottomLeftRadius:
                                                                                          '5px',
                                                                                      }}
                                                                                    >
                                                                                      PNR
                                                                                    </div>
                                                                                    <div
                                                                                      style={{
                                                                                        fontSize:
                                                                                          '12px',
                                                                                        fontWeight:
                                                                                          '600',
                                                                                        minWidth:
                                                                                          '45px',
                                                                                        backgroundColor:
                                                                                          '#fff',
                                                                                        color:
                                                                                          'var(--color-primary-default)',
                                                                                        padding:
                                                                                          '5px',
                                                                                        borderTopRightRadius:
                                                                                          '5px',
                                                                                        borderBottomRightRadius:
                                                                                          '5px',
                                                                                      }}
                                                                                    >
                                                                                      {
                                                                                        flightData?.pnr
                                                                                      }
                                                                                    </div>
                                                                                  </div>
                                                                                )}
                                                                                <div
                                                                                  style={{
                                                                                    fontSize:
                                                                                      '12px',
                                                                                    marginTop:
                                                                                      '5px',
                                                                                    color:
                                                                                      flightData?.refund
                                                                                        ?.toLowerCase()
                                                                                        .includes(
                                                                                          'non refundable',
                                                                                        )
                                                                                        ? 'red'
                                                                                        : flightData?.refund
                                                                                              ?.toLowerCase()
                                                                                              .includes(
                                                                                                'refundable',
                                                                                              )
                                                                                          ? 'green'
                                                                                          : 'inherit',
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    flightData?.refund
                                                                                  }
                                                                                </div>
                                                                              </td>
                                                                              <td
                                                                                style={{
                                                                                  width:
                                                                                    '30%',
                                                                                }}
                                                                              >
                                                                                <div>
                                                                                  <div
                                                                                    style={{
                                                                                      fontSize:
                                                                                        '16px',
                                                                                      fontWeight:
                                                                                        '500',
                                                                                    }}
                                                                                  >
                                                                                    {
                                                                                      flightData?.departure_city_name
                                                                                    }{' '}
                                                                                    <span
                                                                                      style={{
                                                                                        fontSize:
                                                                                          '12px',
                                                                                        fontWeight:
                                                                                          '400',
                                                                                      }}
                                                                                    >
                                                                                      (
                                                                                      {
                                                                                        flightData?.origin
                                                                                      }

                                                                                      )
                                                                                    </span>
                                                                                  </div>
                                                                                  <div
                                                                                    style={{
                                                                                      fontSize:
                                                                                        '12px',
                                                                                    }}
                                                                                  >
                                                                                    {
                                                                                      flightData?.departure_date
                                                                                    }{' '}
                                                                                    |{' '}
                                                                                    {
                                                                                      flightData?.departure_time
                                                                                    }
                                                                                  </div>

                                                                                  <div
                                                                                    style={{
                                                                                      fontSize:
                                                                                        '12px',
                                                                                      marginTop:
                                                                                        '10px',
                                                                                    }}
                                                                                  >
                                                                                    {`${flightData?.departure_airport_name} , ${flightData?.departure_country_name}`}
                                                                                    <br></br>
                                                                                    {`${flightData?.origin_terminal}`}
                                                                                  </div>
                                                                                </div>
                                                                              </td>
                                                                              <td
                                                                                style={{
                                                                                  width:
                                                                                    '20%',
                                                                                }}
                                                                              >
                                                                                <div
                                                                                  style={{
                                                                                    width:
                                                                                      '100px',
                                                                                    fontSize:
                                                                                      '12px',
                                                                                  }}
                                                                                >
                                                                                  <img
                                                                                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB8AAAAfCAYAAAAfrhY5AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMLSURBVHgBtVfdQdswEP7u7PS1GSEbYDZw6GPL3wTABA0TJJmAMgFhAii0jxB3A7qBu0H6XCz1JDu2HP/EgeR7iGPrTt/dSbo7ETaAvgv78gjgcQCNPjx8RIK/ICyQqBcZe6HTaNF1PuoiJKQhfBqLeNhBOoKmWzp6nq2TpLWkHt+I1ACbghBDYdpmBDWQSkh5LKOj6qBMyhDvJNwKaYgZe9A6ANGghmGGf+qybjmoltj35vIvcD4bxWtARXQYRWiA6A7g0USMOFthicWAoRgQo4m8nljW8FVfrCq2odaI1IB9NwJ+SavHVzZ8BaZ0OJ80E/CJ7PL7ikfp+7l+PDDPcaog+8anO/k3XMpxPtnDwbkInJeJnydogsdz8eZKDJ43iWT6U+dLKDyjCnluoZXBrJU4lRmkVrefhGyea0dvnOWLlNx6vZyMtFmbKbaJVzUBsFzrPphHOTlcr5WebrK5usBuMuV4z/hqH/pnGJSSSCJneBdQ6hsc7/VjGLKQhYWEjrbt9RLWe3Yc0xyw/AzyD4p+YZdIVDG/ZEUfTHtmy6bmSSJ4A/TjsHzcGpOSF+dcWg/KSYZUjUIXrFS7nqmAuKiIJckCfnG6uTToLsF6wqhxSKlOy+eL4B8J/fK9j46gw6eh2bGVAcaCPkcvtUpe2TkJO8WO4h42QFuFq4VGkJcyRb8ZrCJn8AS7BOE4/69UxHiFCVHp8GMHsF1RuSOKOEt9t8U3GmMX6LFb22eGN93trO4d8lD/CM+wRVTKtVa3qQ25wHDmdB4LqUT720i1WdMxL6qmeP3l2eaA4pwneiRZJ87e+qZJsIpbJS6X65zcrr2nTwtNUTAGPHx60wnI2u55aZMpvnSjWe1eH0JpLKRXd5G2v53qvPW2J213uSUz63xBR9GsPG3dBOa4abqp9uH2NvI9uxrFxphsaUyfL0dJH9fcahaiI8RP96s81OrBB+lm1XsST3vbvfaulvZ35hToEJ0hpJB2bE367XRRtNPZnStRIE9Cq6QAUdHfmysUSabUUs3k2TXn/wcmBllCD/deIAAAAABJRU5ErkJggg=="
                                                                                    style={{
                                                                                      width:
                                                                                        '16px',
                                                                                      height:
                                                                                        '16px',
                                                                                      marginLeft:
                                                                                        '13px',
                                                                                    }}
                                                                                  />
                                                                                  <div>
                                                                                    {
                                                                                      flightData?.duration
                                                                                    }
                                                                                  </div>
                                                                                </div>
                                                                              </td>
                                                                              <td
                                                                                style={{
                                                                                  width:
                                                                                    '30%',
                                                                                }}
                                                                              >
                                                                                <div>
                                                                                  <div
                                                                                    style={{
                                                                                      fontSize:
                                                                                        '16px',
                                                                                      fontWeight:
                                                                                        '500',
                                                                                    }}
                                                                                  >
                                                                                    {
                                                                                      flightData?.arrival_city_name
                                                                                    }{' '}
                                                                                    <span
                                                                                      style={{
                                                                                        fontSize:
                                                                                          '12px',
                                                                                        fontWeight:
                                                                                          '400',
                                                                                      }}
                                                                                    >
                                                                                      (
                                                                                      {
                                                                                        flightData?.destination
                                                                                      }

                                                                                      )
                                                                                    </span>
                                                                                  </div>
                                                                                  <div
                                                                                    style={{
                                                                                      fontSize:
                                                                                        '12px',
                                                                                    }}
                                                                                  >
                                                                                    {
                                                                                      flightData?.arrival_date
                                                                                    }{' '}
                                                                                    |{' '}
                                                                                    {
                                                                                      flightData?.arrival_time
                                                                                    }
                                                                                  </div>

                                                                                  <div
                                                                                    style={{
                                                                                      fontSize:
                                                                                        '12px',
                                                                                      marginTop:
                                                                                        '10px',
                                                                                    }}
                                                                                  >
                                                                                    {`${flightData?.arrival_airport_name} , ${flightData?.arrival_country_name}`}
                                                                                    <br></br>
                                                                                    {`${flightData?.destination_terminal}`}
                                                                                  </div>
                                                                                </div>
                                                                              </td>
                                                                            </tr>
                                                                          </table>
                                                                        </td>
                                                                      </tr>
                                                                      {flightIndex !==
                                                                        booking
                                                                          ?.layOverData
                                                                          ?.length -
                                                                          1 && (
                                                                        <>
                                                                          <tr height="5"></tr>
                                                                          <tr>
                                                                            <td
                                                                              style={{
                                                                                fontSize:
                                                                                  '10px',
                                                                                backgroundColor:
                                                                                  'var(--color-ticket-background-light)',
                                                                                padding:
                                                                                  '3px',
                                                                                textAlign:
                                                                                  'center',
                                                                                borderRadius:
                                                                                  '20px',
                                                                              }}
                                                                            >
                                                                              {booking
                                                                                ?.layOver[
                                                                                layoverIndex
                                                                              ]
                                                                                ?.layoverTime &&
                                                                              !booking?.layOver[
                                                                                layoverIndex
                                                                              ]?.layoverTime.includes(
                                                                                'NaN',
                                                                              )
                                                                                ? booking
                                                                                    ?.layOver[
                                                                                    layoverIndex
                                                                                  ]
                                                                                    ?.layoverTime
                                                                                : '00'}{' '}
                                                                              in{' '}
                                                                              {
                                                                                booking
                                                                                  ?.layOver[
                                                                                  layoverIndex
                                                                                ]
                                                                                  ?.city
                                                                              }{' '}
                                                                              (Plane
                                                                              change)
                                                                            </td>
                                                                          </tr>
                                                                        </>
                                                                      )}
                                                                      {flightIndex ===
                                                                        booking
                                                                          .layOverData
                                                                          ?.length -
                                                                          1 && (
                                                                        <>
                                                                          <tr height="5"></tr>
                                                                          <tr>
                                                                            <td>
                                                                              <div
                                                                                style={{
                                                                                  width:
                                                                                    '100%',
                                                                                  border:
                                                                                    '1px solid #EA233026',
                                                                                  borderLeft:
                                                                                    'none',
                                                                                  borderRight:
                                                                                    'none',
                                                                                  padding:
                                                                                    '10px',
                                                                                  display:
                                                                                    'flex',
                                                                                  alignItems:
                                                                                    'center',
                                                                                  gap: '50px',
                                                                                }}
                                                                              >
                                                                                <div
                                                                                  style={{
                                                                                    display:
                                                                                      'flex',
                                                                                    alignItems:
                                                                                      'center',
                                                                                    gap: '5px',
                                                                                    fontSize:
                                                                                      '12px',
                                                                                    width:
                                                                                      'max-content',
                                                                                  }}
                                                                                >
                                                                                  {/* <Luggage className="h-4 w-4" />{' '} */}
                                                                                  <img
                                                                                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAApCAYAAABHomvIAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAALGSURBVHgB7VhLctNAEO0emRRLc4L4CAa8YIdithRxThD7BJATmJzA5ASYGzjJGtvsqMIEcYIoN9CShTTNG0kjK04KpCiJvNBbzVd66ta8qddM94BX7qBz2/j35cynimAqia47aLeUfi/EQ2zuFNrE5DHJ9MfXsxMqiVIEDbknjixEqEt3AYiGEe95y1lQdEuLSsBRMsnICS2J5ZtpahL/tvWKuBMvFT5kRrSxF9EfY+iICqJwBBG9TkvJZcKNZj/npwdUAr03+wtsdE071PysaBQVFYTjhFlaWUel/yWK+Dh7FiVEi6AwQaSpbds7aueSSiIk8rNnqahddF9hgnVh6wneOCTJYdCHmOriMHRzC01a4tRILl0lX9ZJmwGeER8SnO4AMhBAEbxQqxNvQ9yvEez1300w9IFqhXxczc+yA5URfNnf/4zOcL0O6g9hjXWPk0hifkoVIUkWBmkHWoroCSLL62xhzRQyNsoI9vqDsWFuiYXCBzbUiCrG2Ygrreanpa/GTeT1VJMeXczPp3bcYVnEgh5DH63m55+Uub5wTw5jbkI+yO1593DJl4V5ZyT83HBIRtTYcFMQYFfSnxdad1zmnnwAkgELj9Juu0VhV7GoLPcaVxjVDAi6l3UUd1X60xoEdUbP4joHbiscipggp7q0DbA6y8S7zVVXFQ3BqmgIVkVDsCoaglXREKyKhmBVbCVBa0+F5ErBpPhJp2Ct75Gh8qWznvvWpZrRcweubYtWntLaWfsQdsZUN1gyDhEcgDIeALlOymlMLnxwLSSNxYwrG5yU5gwnY0XZTsI0/7KmGYM+FsyQ/ism9ZrTSoAx2lQdMEJqkpCgqZD+jUrsLt46JFv7gTc2Hjm2obkv2HD2NQHlEBQPRrZ4cKOU8aI/GMIsH9py7eNwIhh28rTwl4vlbJqf+2etxaT+6do3Pwj+/MeP/wVAFxdC/jfz3gAAAABJRU5ErkJggg=="
                                                                                    style={{
                                                                                      width:
                                                                                        '15px',
                                                                                      height:
                                                                                        '15px',
                                                                                    }}
                                                                                  />{' '}
                                                                                  Check-in
                                                                                  Baggage:{' '}
                                                                                  {
                                                                                    flightData?.checkInbaggage
                                                                                  }
                                                                                </div>

                                                                                <div
                                                                                  style={{
                                                                                    display:
                                                                                      'flex',
                                                                                    alignItems:
                                                                                      'center',
                                                                                    gap: '5px',
                                                                                    fontSize:
                                                                                      '12px',
                                                                                    width:
                                                                                      'max-content',
                                                                                  }}
                                                                                >
                                                                                  {/* <Briefcase className="h-4 w-4" />{' '} */}
                                                                                  <img
                                                                                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACEAAAAvCAYAAABt7qMfAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAALASURBVHgB7VhLctNAEO1p2SzYYE6AcwJM7LBgw5TZUQFkLmBzAsgJICfAnICcgNjA2taKTRyiG0Q5QcyGBWVP0zPWqOxExKNEcpEqvSpbo5mu1tN8nrpbQEY0pF+rwp86AdbSxifB9wAyQmQxfiw7XYWqz83aFQ4jUKJzFAxCyJsEz0C9gnTqaD6dKbEVBoOpi3EFHOEhvU9uCAIh+I0vQBFI7q9zs1bBeY+vfciThHFOcZvE3tH48nQ3n+36QN6hsQF8CI5AuAaqiOdp/WJecZr+XEjkjZKExX9B4pJOtOSuFOh1icxRW7ZsgBUpPqKp3pDHydgZ0WIf0aoPCkGp4UVVTUgYMRL0mXskFA7qzxTuWzHDmECtijTeDAEN8Y5f+DC503+t9suPesBwZMklJfbvAAwgR8x5KQnB5yckyqtAvfk5+nYQK6boJcZKPOJpiqAYhNvSjxB52RlI2OXLgWhJX4JeCg2i4WT81YeC0Wy/OuUlqOs2f+jurxxRIcQ5bADLH7+7iPdKsbLIRGJHPm9oPbnKRh93LXhQBAl9jAmrJ1W7idMJLKIv9Mbb7Rc9yJsE7yYjx6wj9Sft1w/STCqLHW+k3QN8Co4oN6ZFScKiJGFRkrAoSVjcLhIcvk/ja/Rj9OUszWYGkOSiHKeegSOcSXDsuaf4NyfR+ZcNx6YhP7yj7SbB4AM4wrk0EAe/a+sNx8Egc5RebkwLXN5MG0RSePut1C89E5Ht4KjJXxdD3hTGf5w065Om81E0SalIsmyTkxZFRAfAyzEq174+matlxwHqCazWJ6eU41KJhe/Ev56F4/FwKx5bYEf6DcWZclwCLBYE4Yz1xua8K0USMyO8L0jQ20LI8MM5ah9ORqtCllrR5RyDjYRJ4W36DteEi69UnVCAUdJWXgA3wLIvT3nO9W6DpvR9UzbIAet8/QWF/QJXammC5QAAAABJRU5ErkJggg=="
                                                                                    style={{
                                                                                      width:
                                                                                        '15px',
                                                                                      height:
                                                                                        '15px',
                                                                                    }}
                                                                                  />{' '}
                                                                                  Cabin
                                                                                  Baggage:{' '}
                                                                                  {
                                                                                    flightData?.handBaggage
                                                                                  }
                                                                                </div>
                                                                                {ticketData
                                                                                  ?.booking_details
                                                                                  ?.cabin && (
                                                                                  <div
                                                                                    style={{
                                                                                      display:
                                                                                        'flex',
                                                                                      alignItems:
                                                                                        'center',
                                                                                      gap: '5px',
                                                                                      fontSize:
                                                                                        '12px',
                                                                                      width:
                                                                                        'max-content',
                                                                                    }}
                                                                                  >
                                                                                    {/* <Char className="h-4 w-4" />{' '} */}
                                                                                    <img
                                                                                      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACMAAAAqCAYAAAA51uKRAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAPKSURBVHgBzVhRbtNAEJ0ZG77DCTAnIKVFQvzghl+ggQvQngC4AT0B3ID2ApAA4o/E/CCkRmBO0HCChj9E6x1mNrazce3EkeKIJzX17mY9z7Mzb8ZBcHAn7AYJ/G3p9Un0KYYNA2+H3WeM3AWEtoxb7iIDHCUGD+OoN4YNgNizRMIiEcsUYP8K8bAtHoMNgJhh4ox7xuAL8cmhzI91QrwT+MhvYAMgMfcrG7DB4+9R7/Vo8P5lwriVEVLP7YQPQmgYxMB5oCIlQXYtcTJBxoNszERdaBhkjBflBgEDd3EU9SI5prFeC7F70DSZNFMmqcGbl7/CfftPsk0CuQUNglJDcWaw+AX3GK9IMEPTZJj5Zzpuie7MEZo7RkpCaJoMoImyiYSSOTJzxwh0ExqEJZMkfpRNeECXAzU7RrDi2CwZTeMsa8oMZseoAthkEJNj8UuVQTeIfbhoQ0PIySBTpUE3iIGweTIXAFGVQTeIgTGEpsmIwXiRQc7IYnNK7M+NNGtYArjEIBr8IpVM61Nrp7P3DmGu2udgp/DK+Uaj6GMENYHuYLvz6DUCPtPrq+AFXwdv8xtv33/QRfbewYoQA+Nzg7t1GjSa3zkTvz9wvusuuVq0Cmw/RPyjqOxLybgGxUPFIJ5pEUPkG7xR9Xdh8Joc66wfkqOV1vYVLIFfNLjd2RuLa4PSCq5ahBggQvBtuNTtsejVlnSJp0pGGzT1zsk0UZaTcQ2WVXCrRchP1fW3O3s1WlHWD/WoFdG07tUnkxmEtIIXniR2zOzDmuGXzM0MTluGfCzCGPszMprapeldBTTeZCUyrsFiy6AxtXN/T7WoLZHfOxn0D2CNoOKEGlzUMrBJKzhD7RqlhbdOtceySVf8NE0twRQ7Yfe5KLFN07xZr9+OTmRTbNAcfx98OCouUtkOt2XwCt4xlEycJwlWIKKwKU5AbyQbT4tvqmUBbFsGIk7pmlA+e+6aR3wES2CAf7v1S9MbUbSLpw+XKbNqUVYqsOpmUgzP7JOI2o6G/V1YE9Qb/vSYuzBlEI8+97f0kqo2zVqG+oFaB+oFicMDjZ3UUPtW5+H+QjK2ZZiidbfz5Dqsl9DkgvFxNiampwvJgCN2xQq+JkLj4stjZcyoLsjZnsGGMBr0sdIzhdeXjcBfuKo/HJHJA1iE0Fbs6YgPYWVYIW1pn4PIx5dWYQVIXRpmOqFuhRUhvdKpFckKuVgcM8UXNvRe5aluktWDmmwP3bJpzckLd0kb98pjkoW2bB5C9Y2r15ZBHwgv7UeC/wiVnrF9jbxiwAbxD9Sgs/pIoFO9AAAAAElFTkSuQmCC"
                                                                                      style={{
                                                                                        width:
                                                                                          '15px',
                                                                                        height:
                                                                                          '15px',
                                                                                      }}
                                                                                    />{' '}
                                                                                    {
                                                                                      booking?.cabin
                                                                                    }
                                                                                  </div>
                                                                                )}
                                                                              </div>
                                                                            </td>
                                                                          </tr>
                                                                        </>
                                                                      )}
                                                                    </React.Fragment>
                                                                  </React.Fragment>
                                                                );
                                                              },
                                                            )}
                                                          </table>
                                                        </td>
                                                      </tr>
                                                      <tr height="15"></tr>
                                                      <tr>
                                                        <td>
                                                          <table width="100%">
                                                            <tr>
                                                              {booking?.passenger_details?.list_header?.map(
                                                                (
                                                                  item,
                                                                  headerIndex,
                                                                ) => (
                                                                  <td
                                                                    key={`header-${headerIndex}-${item.headerName}`}
                                                                    style={{
                                                                      color:
                                                                        '#808080',
                                                                      fontSize:
                                                                        '12px',
                                                                      fontWeight:
                                                                        '600',
                                                                      width:
                                                                        '20%',
                                                                    }}
                                                                  >
                                                                    {
                                                                      item?.headerName
                                                                    }
                                                                  </td>
                                                                ),
                                                              )}
                                                            </tr>

                                                            {booking?.passenger_details?.list_body?.map(
                                                              (
                                                                passenger: {
                                                                  name: string;
                                                                  [
                                                                    key: string
                                                                  ]: any;
                                                                },
                                                                passengerIndex: number,
                                                              ) => (
                                                                <tr
                                                                  key={`passenger-${passengerIndex}-${passenger.name}`}
                                                                >
                                                                  {booking?.passenger_details?.list_header?.map(
                                                                    (
                                                                      header: {
                                                                        template: string;
                                                                      },
                                                                      columnIndex: number,
                                                                    ) => (
                                                                      <td
                                                                        key={`passenger-${passengerIndex}-column-${columnIndex}`}
                                                                        height="40"
                                                                        className="text-base"
                                                                        style={{
                                                                          fontSize:
                                                                            '12px',
                                                                          fontWeight:
                                                                            '600',
                                                                          width:
                                                                            '20%',
                                                                        }}
                                                                      >
                                                                        {
                                                                          passenger[
                                                                            header
                                                                              .template
                                                                          ]
                                                                        }

                                                                        {header?.template ===
                                                                          'name' &&
                                                                          passenger?.ticket_no && (
                                                                            <div
                                                                              style={{
                                                                                fontSize:
                                                                                  '10px',
                                                                                fontWeight:
                                                                                  '600',
                                                                              }}
                                                                            >
                                                                              <span
                                                                                style={{
                                                                                  fontSize:
                                                                                    '10px',
                                                                                  color:
                                                                                    'var(--color-text-secondary)',
                                                                                }}
                                                                              >
                                                                                Ticket
                                                                                No:{' '}
                                                                              </span>{' '}
                                                                              {
                                                                                passenger?.ticket_no
                                                                              }
                                                                            </div>
                                                                          )}
                                                                      </td>
                                                                    ),
                                                                  )}
                                                                </tr>
                                                              ),
                                                            )}
                                                          </table>
                                                        </td>
                                                      </tr>
                                                    </table>
                                                  </td>
                                                </tr>
                                                <tr height="20px">
                                                  <td
                                                    style={{
                                                      borderBottom:
                                                        '2px dashed  #B6B6B6',
                                                    }}
                                                  ></td>
                                                </tr>
                                                {/* <tr>
                                          <td width="100%">
                                              <table width="100%">
                                              <tr>
                                                <td>
                                                  <div
                                                    style={{
                                                      margin: '0 -45px',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        backgroundColor:
                                                            '#EA233026',
                                                        height: '50px',
                                                        width: '50px',
                                                        borderRadius: '50%',
                                                      }}
                                                    ></div>
                                                    <div
                                                      style={{
                                                        borderTop:
                                                          '1.5px dashed #808080',
                                                        width: '100%',
                                                      }}
                                                    ></div>
                                                    <div
                                                      style={{
                                                        backgroundColor:
                                                            '#EA233026',
                                                        height: '50px',
                                                        width: '50px',
                                                        borderRadius: '50%',
                                                      }}
                                                    ></div>
                                                  </div>
                                                </td>
                                              </tr>
                                            </table>
                                          </td>
                                        </tr> */}
                                                <tr>
                                                  <td>
                                                    <table width="100%">
                                                      <tr height="50px">
                                                        <td
                                                          style={{
                                                            width: '100%',
                                                          }}
                                                        >
                                                          <table width="100%">
                                                            <tr>
                                                              <td>
                                                                <div
                                                                  style={{
                                                                    fontSize:
                                                                      '12px',
                                                                    color:
                                                                      '#808080',
                                                                    fontWeight:
                                                                      '500',
                                                                  }}
                                                                >
                                                                  Barcode(s) for
                                                                  your journey{' '}
                                                                  <span
                                                                    style={{
                                                                      fontWeight:
                                                                        '600',
                                                                    }}
                                                                  >
                                                                    {
                                                                      ticketData
                                                                        ?.booking_details
                                                                        ?.departure_city
                                                                    }
                                                                  </span>{' '}
                                                                  -{' '}
                                                                  <span
                                                                    style={{
                                                                      fontWeight:
                                                                        '600',
                                                                    }}
                                                                  >
                                                                    {
                                                                      ticketData
                                                                        ?.booking_details
                                                                        ?.arrival_city
                                                                    }
                                                                  </span>{' '}
                                                                  on{' '}
                                                                  <span
                                                                    style={{
                                                                      fontWeight:
                                                                        '600',
                                                                    }}
                                                                  >
                                                                    {
                                                                      ticketData
                                                                        ?.booking_details
                                                                        ?.airline_name
                                                                    }
                                                                  </span>
                                                                </div>
                                                              </td>
                                                            </tr>
                                                          </table>
                                                        </td>
                                                      </tr>
                                                      <tr>
                                                        <td>
                                                          <table width="100%">
                                                            {booking?.passenger_details?.list_body?.map(
                                                              (
                                                                passenger: {
                                                                  name: string;
                                                                  [
                                                                    key: string
                                                                  ]: any;
                                                                },
                                                                passengerIndex: number,
                                                              ) => (
                                                                <React.Fragment
                                                                  key={`passenger-${passengerIndex}-${passenger.name}`}
                                                                >
                                                                  <tr>
                                                                    <td
                                                                      style={{
                                                                        width:
                                                                          'max-content',
                                                                        fontWeight:
                                                                          '600',
                                                                        fontSize:
                                                                          '12px',
                                                                      }}
                                                                    >
                                                                      {
                                                                        passenger?.name
                                                                      }
                                                                    </td>
                                                                    <td
                                                                      style={{
                                                                        width:
                                                                          '200px',
                                                                        height:
                                                                          '60px',
                                                                        display:
                                                                          'flex',
                                                                        justifyContent:
                                                                          'center',
                                                                        alignItems:
                                                                          'center',
                                                                      }}
                                                                    >
                                                                      {passenger?.barCode ? (
                                                                        <Image
                                                                          src={
                                                                            passenger?.barCode
                                                                          }
                                                                          quality={
                                                                            100
                                                                          }
                                                                          alt="barcode"
                                                                          width={
                                                                            200
                                                                          }
                                                                          height={
                                                                            60
                                                                          }
                                                                          crossOrigin="anonymous"
                                                                          unoptimized={
                                                                            true
                                                                          }
                                                                          onError={(
                                                                            e: React.SyntheticEvent<
                                                                              HTMLImageElement,
                                                                              Event
                                                                            >,
                                                                          ) => {
                                                                            console.error(
                                                                              'Barcode image failed to load:',
                                                                              e
                                                                                .target
                                                                                .src,
                                                                            );
                                                                            // Try to reload or show fallback
                                                                            const img =
                                                                              e.target as HTMLImageElement;
                                                                            img.style.display =
                                                                              'none';
                                                                            // Create fallback text
                                                                            const fallback =
                                                                              document.createElement(
                                                                                'div',
                                                                              );
                                                                            fallback.textContent =
                                                                              'Barcode not available';
                                                                            fallback.style.cssText =
                                                                              'text-align: center; color: #666; font-size: 12px; padding: 10px; height: 60px; display: flex; align-items: center; justify-content: center;';
                                                                            img.parentNode?.appendChild(
                                                                              fallback,
                                                                            );
                                                                          }}
                                                                          onLoad={() => {
                                                                            console.log(
                                                                              'Barcode image loaded successfully:',
                                                                              passenger?.barCode,
                                                                            );
                                                                          }}
                                                                        />
                                                                      ) : (
                                                                        <div
                                                                          style={{
                                                                            width:
                                                                              '200px',
                                                                            height:
                                                                              '60px',
                                                                            display:
                                                                              'flex',
                                                                            alignItems:
                                                                              'center',
                                                                            justifyContent:
                                                                              'center',
                                                                            color:
                                                                              '#666',
                                                                            fontSize:
                                                                              '12px',
                                                                            border:
                                                                              '1px dashed #ccc',
                                                                          }}
                                                                        >
                                                                          No
                                                                          barcode
                                                                          available
                                                                        </div>
                                                                      )}
                                                                    </td>
                                                                    <td></td>
                                                                  </tr>
                                                                </React.Fragment>
                                                              ),
                                                            )}
                                                          </table>
                                                        </td>
                                                      </tr>
                                                    </table>
                                                  </td>
                                                </tr>
                                              </table>
                                            );
                                          },
                                        )}
                                      </td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr height="30"></tr>
            <tr>
              <td className="w-full">
                <div className="rounded border p-3">
                  <h3
                    style={{
                      fontWeight: '600',
                      fontSize: '16px',
                      marginBottom: '10px',
                    }}
                  >
                    IMPORTANT INFORMATION
                  </h3>

                  <ul
                    style={{
                      fontSize: '12px',
                      listStyleType: 'disc',
                      margin: '0 20px',
                      lineHeight: '1.8',
                    }}
                  >
                    <li>
                      <strong>Transit Visa Requirement : </strong> Please note
                      that travellers are solely responsible for ensuring their
                      eligibility to enter the destination or transit countries.
                      We accept no liability in this regard. Please check the
                      travel rules of all regulatory websites before commencing
                      travel.
                    </li>
                    <li>
                      <strong>
                        Ensure Compliance with Visa/Transit Visa Requirements
                        :{' '}
                      </strong>
                      Please ensure you verify and adhere to the visa and
                      transit visa requirements based on your nationality,
                      passport type, and destination country. For the most
                      reliable and up-to-date visa requirements, check reputable
                      regulatory websites like IATA or with the airline.
                    </li>
                    <li>
                      <strong>Items not allowed in the aircraft : </strong>
                      Lighters, matchsticks, flammable liquids, toxic,
                      corrosives, pepper spray, flammable gas, e-cigarette,
                      infectious substances, radioactive materials, explosive
                      ammunitions
                    </li>
                    <li>
                      <strong>Items allowed only in Hand-baggage : </strong>
                      Lithium batteries, power banks
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </table>
        )}
      </div>
    </div>
  );
};

export default Ticket;
